import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

type Props = {
  onDone?: () => void;
  durationMs?: number;
};

type ConfettiPiece = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
  color: string;
  shape: "rect" | "circle";
};

const CONFETTI_COLORS = [
  "#f59e0b", // amber
  "#ef4444", // red
  "#22c55e", // green
  "#3b82f6", // blue
  "#a855f7", // purple
  "#ec4899", // pink
  "#eab308", // yellow
];

export default function RewardOverlay({ onDone, durationMs = 3500 }: Props) {
  const doneRef = useRef(false);

  const pieces = useMemo<ConfettiPiece[]>(() => {
    return Array.from({ length: 90 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 2.2 + Math.random() * 1.6,
      size: 7 + Math.random() * 8,
      rotation: Math.random() * 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      shape: Math.random() > 0.25 ? "rect" : "circle",
    }));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
    }, durationMs);

    return () => window.clearTimeout(t);
  }, [onDone, durationMs]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden">
      <style>
        {`
          @keyframes reward-confetti-fall {
            0% {
              transform: translate3d(0, -12vh, 0) rotate(0deg);
              opacity: 1;
            }
            80% {
              opacity: 1;
            }
            100% {
              transform: translate3d(var(--drift), 110vh, 0) rotate(720deg);
              opacity: 0;
            }
          }

          @keyframes reward-pop {
            0% {
              transform: scale(0.92);
              opacity: 0;
            }
            12% {
              transform: scale(1);
              opacity: 1;
            }
            85% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        `}
      </style>

      {/* Optional celebration message */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="rounded-2xl bg-white/90 px-6 py-4 text-center shadow-lg"
          style={{
            animation: "reward-pop 3.2s ease-out forwards",
          }}
        >
          <div className="text-2xl font-bold text-gray-900">
            Great job!
          </div>
          <div className="mt-1 text-sm text-gray-600">
            Homework completed
          </div>
        </div>
      </div>

      {pieces.map((piece) => {
        const drift = `${Math.random() * 160 - 80}px`;

        return (
          <span
            key={piece.id}
            className="absolute top-0 block"
            style={{
              left: `${piece.left}%`,
              width: `${piece.size}px`,
              height:
                piece.shape === "circle"
                  ? `${piece.size}px`
                  : `${piece.size * 1.6}px`,
              borderRadius: piece.shape === "circle" ? "9999px" : "2px",
              backgroundColor: piece.color,
              transform: `rotate(${piece.rotation}deg)`,
              animationName: "reward-confetti-fall",
              animationDuration: `${piece.duration}s`,
              animationDelay: `${piece.delay}s`,
              animationTimingFunction: "linear",
              animationFillMode: "forwards",
              ["--drift" as string]: drift,
            }}
          />
        );
      })}
    </div>,
    document.body
  );
}